// ============================================================================
// BUILD FULL RECEIPT MODEL — Combines non-PHI + PHI into a single receipt object
//
// Called only when gp_employee clicks "Download Full Receipt PDF (PHI)".
// All PHI must already be loaded by the caller via Cloud Functions before
// passing to this function. This function is a pure transformation — no I/O.
//
// INVARIANT: PHI data passed in came exclusively from Cloud Functions.
// This file never imports phiApi or makes any network calls.
// ============================================================================

const ATT_TYPE_LABELS = {
  LABS:    'Lab Results',
  IMAGING: 'Imaging Report',
  PDF:     'PDF Document',
  PHOTO:   'Photo / Scan',
  OTHER:   'Other',
};

// Phase 1.5: human classification labels carried on promoted packet documents.
const ATT_DOCUMENT_TYPE_LABELS = {
  REFERRAL_LETTER:   'Referral letter',
  CLINICAL_NOTE:     'Clinical note',
  LAB_RESULT:        'Lab result',
  IMAGING_REPORT:    'Imaging report',
  INSURANCE_CARD:    'Insurance card',
  MEDICATION_LIST:   'Medication list',
  DISCHARGE_SUMMARY: 'Discharge summary',
  CONSULT_NOTE:      'Consult note',
  OTHER:             'Other',
  UNKNOWN:           'Unknown',
};

/**
 * Resolve the best display label for a referral attachment. Prefers the human
 * classification (documentType) carried from a promoted packet document; falls
 * back to the neutral upload-type label. Never uses the filename.
 */
function resolveAttachmentLabel(att) {
  const dt = att.documentType;
  if (dt && dt !== 'UNKNOWN' && ATT_DOCUMENT_TYPE_LABELS[dt]) {
    return ATT_DOCUMENT_TYPE_LABELS[dt];
  }
  return ATT_TYPE_LABELS[att.type] || att.type || 'Other';
}

/**
 * Converts a Firestore Timestamp-like value to milliseconds (for new Date(ms)).
 * Handles: number (ms), ISO string, Firestore Timestamp { seconds, nanoseconds }
 * or the serialized form { _seconds, _nanoseconds } returned by Firebase Functions.
 */
function tsToMs(ts) {
  if (!ts) return null;
  if (typeof ts === 'number') return ts;
  if (typeof ts === 'string') { const ms = Date.parse(ts); return isNaN(ms) ? null : ms; }
  // Firestore Timestamp object or its serialized form
  const secs = ts.seconds ?? ts._seconds ?? null;
  if (secs !== null) return secs * 1000;
  return null;
}

/**
 * Format a date-of-birth value as MM/DD/YYYY.
 * Handles ISO date strings (YYYY-MM-DD) without timezone shifting,
 * and Firestore Timestamp objects as a fallback.
 */
function formatDob(dob) {
  if (!dob) return '—';
  const s = String(dob).trim();
  // ISO date-only string (no time component) — split directly to avoid UTC→local shift
  const isoDate = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDate) return `${isoDate[2]}/${isoDate[3]}/${isoDate[1]}`;
  // Fallback: parse as timestamp (handles ISO datetime strings or Timestamp objects)
  const ms = tsToMs(dob);
  if (ms) {
    const d = new Date(ms);
    return [
      String(d.getMonth() + 1).padStart(2, '0'),
      String(d.getDate()).padStart(2, '0'),
      d.getFullYear(),
    ].join('/');
  }
  return s;
}

/**
 * Normalise any timestamp value to an ISO string, or return fallback.
 */
function toIsoString(ts, fallback = null) {
  const ms = tsToMs(ts);
  return ms ? new Date(ms).toISOString() : fallback;
}

const OUTCOME_LABELS = {
  routine: 'ROUTINE',
  urgent:  'URGENT',
  not_yet: 'NOT YET',
};

const SEX_LABELS = {
  male:    'Male',
  female:  'Female',
  other:   'Other',
  unknown: 'Unknown / Not specified',
};

/**
 * Builds a single receipt model object suitable for PDF rendering.
 *
 * @param {object} params
 *
 * Non-PHI params (always available):
 * @param {string}  params.runId
 * @param {string}  params.referralId
 * @param {string}  params.tenantId
 * @param {string}  params.specialtyName
 * @param {string}  params.packName
 * @param {string}  params.packVersion
 * @param {string}  params.outcome          'routine'|'urgent'|'not_yet'
 * @param {number}  params.completenessScore
 * @param {string}  params.visitType
 * @param {string}  params.reasonForEscalation
 * @param {object[]} params.missingFields   [{id, label}]
 * @param {object[]} params.triggeredRedFlags [{id, label}]
 * @param {object[]} params.requiredFields  [{id, label, required}] from pack
 * @param {object}  params.clinicalAnswers  { [fieldId]: value }
 * @param {object|null} params.overrideInfo { reason, note } or null
 * @param {string}  params.submittedAt
 * @param {string}  params.submittedByName
 * @param {string}  params.submittedByRole
 *
 * PHI params (from Cloud Functions — caller's responsibility to load):
 * @param {object|null} params.phiIdentity  { name: {first,last}, dob, sex, identifiers: [{type,value}] }
 * @param {object|null} params.phiContact   { phone, email, address?: {street,city,state,zip} }
 * @param {object|null} params.phiInsurance { payerName, payerId, memberId, groupNumber, planType }
 * @param {object[]}   params.attachments   manifest list from listAttachments() — [{type,status,createdAt,...}]
 *
 * @returns {object} receipt model (plain JS object, no dates, no Timestamps)
 */
export const buildFullReceiptModel = ({
  // Non-PHI
  runId,
  referralId,
  tenantId,
  specialtyName,
  packName,
  packVersion,
  outcome,
  completenessScore,
  visitType,
  reasonForEscalation,
  missingFields = [],
  triggeredRedFlags = [],
  requiredFields = [],
  clinicalAnswers = {},
  overrideInfo = null,
  submittedAt,
  submittedByName,
  submittedByRole,
  // PHI (may be null if load failed — caller decides whether to proceed)
  phiIdentity = null,
  phiContact = null,
  phiInsurance = null,
  attachments = [],
}) => {
  const wasOverridden = Boolean(overrideInfo?.reason);
  const displayOutcome = wasOverridden ? 'routine' : outcome;
  const generatedAt = new Date().toISOString();
  // Normalise submittedAt to an ISO string so renderers never receive a raw
  // Firestore Timestamp, which would produce "Invalid Date".
  const resolvedSubmittedAt = toIsoString(submittedAt, generatedAt);

  // ── Patient block (PHI) ──
  const patient = phiIdentity ? {
    firstName: phiIdentity.name?.first || '',
    lastName:  phiIdentity.name?.last  || '',
    fullName:  [phiIdentity.name?.first, phiIdentity.name?.last].filter(Boolean).join(' ') || '—',
    dob:       formatDob(phiIdentity.dob),
    sex:       SEX_LABELS[phiIdentity.sex] || phiIdentity.sex || '—',
    identifiers: (phiIdentity.identifiers || []).map(id => ({
      type:  id.type || 'ID',
      value: id.value || '',
    })),
  } : null;

  const contact = phiContact ? {
    phone: phiContact.phone || null,
    email: phiContact.email || null,
    address: phiContact.address
      ? [
          phiContact.address.street,
          phiContact.address.city,
          phiContact.address.state,
          phiContact.address.zip,
        ].filter(Boolean).join(', ')
      : null,
  } : null;

  const insurance = phiInsurance ? {
    payerName:   phiInsurance.payerName   || null,
    payerId:     phiInsurance.payerId     || null,
    memberId:    phiInsurance.memberId    || null,
    groupNumber: phiInsurance.groupNumber || null,
    planType:    phiInsurance.planType    || null,
  } : null;

  // ── Criteria block (non-PHI) ──
  const criteriaRows = requiredFields.map(field => {
    const raw = clinicalAnswers[field.id];
    const provided = raw !== undefined && raw !== null && raw !== ''
      && !(Array.isArray(raw) && raw.length === 0);
    let answerText = '';
    if (provided) {
      if (typeof raw === 'boolean')  answerText = raw ? 'Yes' : 'No';
      else if (Array.isArray(raw))   answerText = raw.join(', ');
      else                           answerText = String(raw);
    }
    return {
      label:      field.label,
      helpText:   field.helpText || '',
      provided,
      answerText,
    };
  });

  // Derive completeness: prefer criteria rows when answers are available,
  // otherwise fall back to outcome status (Gate invariant: routine/urgent = 100%).
  const derivedCompleteness = (() => {
    const answeredCount = Object.keys(clinicalAnswers).length;
    if (criteriaRows.length > 0 && answeredCount > 0) {
      return Math.round(criteriaRows.filter(r => r.provided).length / criteriaRows.length * 100);
    }
    if (displayOutcome === 'routine' || displayOutcome === 'urgent') {
      return 100;
    }
    if (displayOutcome === 'not_yet' && requiredFields.length > 0) {
      const filledCount = requiredFields.length - missingFields.length;
      return Math.round((filledCount / requiredFields.length) * 100);
    }
    if (typeof completenessScore === 'number' && completenessScore > 0) {
      return completenessScore;
    }
    return 100;
  })();

  // ── Attachments block (PHI manifest — type/status/size only; no filenames) ──
  const attachmentRows = attachments.map(att => ({
    attachmentId: att.attachmentId || att.id || null,
    typeLabel:  resolveAttachmentLabel(att),
    status:     att.status || 'UNKNOWN',
    sizeBytes:  att.sizeBytes ?? att.uploadedSizeBytes ?? null,
    uploadedAt: tsToMs(att.updatedAt || att.createdAt),
  }));

  return {
    meta: {
      generatedAt,
      runId:      runId || referralId,
      referralId: referralId || '',
      tenantId:   tenantId   || '',
    },
    outcome: {
      label:            OUTCOME_LABELS[displayOutcome] || displayOutcome?.toUpperCase() || '—',
      status:           displayOutcome,
      completenessScore: derivedCompleteness,
      wasOverridden,
      overrideReason:   overrideInfo?.reason  || null,
      overrideNote:     overrideInfo?.note    || null,
    },
    pathway: {
      specialtyName: specialtyName || '—',
      packName:      packName      || '—',
      packVersion:   packVersion   || '—',
      visitType:     visitType     || '—',
      reason:        reasonForEscalation || '—',
    },
    provider: {
      name: submittedByName || '—',
      // role omitted — section heading "Referring Provider" carries that context
    },
    submittedAt: resolvedSubmittedAt,
    // PHI sections (null = not available)
    patient,
    contact,
    insurance,
    // Non-PHI clinical
    criteria: {
      rows:              criteriaRows,
      missingFields:     missingFields.map(f => f.label),
      triggeredRedFlags: triggeredRedFlags.map(rf => rf.label),
    },
    attachments: attachmentRows,
    // PHI presence flags
    hasPhiLoaded: Boolean(patient || contact || insurance),
  };
};
